import { Icon } from "@captions-web/icons";
import { createFileRoute } from "@tanstack/react-router";
import { nanoid } from "nanoid";
import { useState } from "react";

import { DropZone } from "~/components/drop-zone";
import { Button } from "~/components/primitives/button";
import { AudioProcessor } from "~/modules/audio/audio-processor";
import {
  AudioTrimmer,
  TrimmableRegion,
  TrimmableRegion_Id,
} from "~/modules/audio/components/audio-trimmer/audio-trimmer";
import {
  MAX_AUDIO_UPLOAD_SIZE_MS,
  MIN_AUDIO_UPLOAD_SIZE_MS,
  MIN_REGION_SIZE_MS,
  MAX_REGION_SIZE_MS,
} from "~/modules/audio/constants";
import { css } from "~/styled-system/css";

export const Route = createFileRoute("/storybook/audio-trimmer")({
  component: RouteComponent,
});

function RouteComponent() {
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [trimmableDurationRegions, setTrimmableDurationRegions] = useState<TrimmableRegion[]>([]);
  const [trimmableRegions, setTrimmableRegions] = useState<TrimmableRegion[]>([]);

  const handleAudioDrop = async (files: File[]) => {
    if (files.length > 0) {
      // load + parse the audio file to get duration
      const audioProcessor = new AudioProcessor();
      await audioProcessor.loadAudio(files[0]);
      const { durationMs } = audioProcessor.metadata;
      // create one big region for testing duration trimming
      const trimmableDurationRegions = [
        { id: TrimmableRegion_Id(nanoid()), startMs: 0, endMs: durationMs, index: 0 },
      ];
      setTrimmableDurationRegions(trimmableDurationRegions);

      // create 4-second segments for testing region trimming
      // This will be done on the backend, simulating the splitting of the audio into segments here
      const segmentDurationMs = 4000; // 4 seconds in milliseconds
      const totalDurationMs = durationMs ?? 0;
      const segments: TrimmableRegion[] = [];

      // calculate how many complete segments we can have
      const numSegments = Math.ceil(totalDurationMs / segmentDurationMs);
      for (let i = 0; i < numSegments; i++) {
        const start = i * segmentDurationMs;
        const end = Math.min((i + 1) * segmentDurationMs, totalDurationMs);
        segments.push({
          id: TrimmableRegion_Id(nanoid()),
          startMs: start,
          endMs: end,
          index: i,
        });
      }

      setTrimmableRegions(segments);
      setAudioFile(files[0]);
    }
  };

  return (
    <div className={sectionStyles}>
      <h2 className={titleStyles}>Audio Trimmer</h2>
      <div className={audioTrimmerWrapperStyles}>
        {audioFile ? (
          <div>
            <div className={audioTrimmer}>
              <AudioTrimmer
                trimMode="duration"
                audio={audioFile}
                onRegionsChange={setTrimmableDurationRegions}
                initialRegions={trimmableDurationRegions}
                playButtonVariant="inline"
                loopDurationRegion
                minRegionSizeMs={MIN_AUDIO_UPLOAD_SIZE_MS}
                maxRegionSizeMs={MAX_AUDIO_UPLOAD_SIZE_MS}
                bottom={
                  <div className={actionsStyles}>
                    <Button
                      variant="primary"
                      size="md"
                      icon={<Icon name="right-arrow-small" />}
                      iconPosition="after"
                    >
                      Continue
                    </Button>
                  </div>
                }
              />
            </div>
            <div className={audioTrimmer}>
              <footer className={footerStyles}>
                <AudioTrimmer
                  audio={audioFile}
                  trimMode="regions"
                  minRegionSizeMs={MIN_REGION_SIZE_MS}
                  maxRegionSizeMs={MAX_REGION_SIZE_MS}
                  onRegionsChange={setTrimmableRegions}
                  playButtonVariant="bottom"
                  initialRegions={trimmableRegions}
                  bottom={
                    <div className={actionsStyles}>
                      <Button
                        variant="primary"
                        size="md"
                        icon={<Icon name="right-arrow-small" />}
                        iconPosition="after"
                      >
                        Continue
                      </Button>
                    </div>
                  }
                />
              </footer>
            </div>
          </div>
        ) : (
          <DropZone acceptMultiple={false} onDrop={handleAudioDrop} className={dropZoneStyles}>
            {() => (
              <div className={dropZoneContentStyles}>
                <Icon name="upload" size={48} />
                <p>Drag and drop an audio file or click to browse</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "audio/*";
                    input.onchange = (e) => {
                      const files = (e.target as HTMLInputElement).files;
                      if (files) {
                        handleAudioDrop(Array.from(files));
                      }
                    };
                    input.click();
                  }}
                >
                  Browse Files
                </Button>
              </div>
            )}
          </DropZone>
        )}
      </div>
    </div>
  );
}

const sectionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "12px 16px 20px",

  // slightly different background to match that of the design system figma for better contrast
  background: "#181818",
  borderRadius: "8px",
  border: "1px solid token(colors.$gray.800)",
});

const titleStyles = css({
  fontSize: "30px",
  textShadow: "2px 2px 0 token(colors.$purple)",
});

const dropZoneStyles = css({
  width: "100%",
  height: "200px",
  border: "2px dashed $gray.600",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: "$gray.500",
  },
});

const audioTrimmerWrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "auto",
  gap: "16px",
  padding: "16px",
  borderRadius: "8px",
});

const audioTrimmer = css({
  marginBottom: "40px",
});

const dropZoneContentStyles = css({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  color: "$gray.400",
});

const actionsStyles = css({
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
});

const footerStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});
